import { getUserExternalDirectoryId } from 'accelerator-aad/lib/getUserExternalDirectoryId';
import { useLiveQuery } from 'dexie-react-hooks';
import { logGreyError } from 'owa-analytics';
import { getTimestamp, owaDate, startOfDay } from 'owa-datetime';
import { getUserTimeZone } from 'owa-session-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

import { placesPeopleTable } from '../database';
import { fetchCollaborators } from '../utils/fetchCollaborators';
import { fetchTimezoneSettings } from '../utils/fetchTimezoneSettings';
import { usePlacesPeopleCacheMaintenance } from './usePlacesPeopleCacheMaintenance';

import type { OwaDate } from 'owa-datetime';

import type { PeopleInfo } from '../database';
import type { UserEmailAddress } from 'accelerator-aad/lib/UserEmailAddress';
/**
 * @param collaboratorType: The collaborator type we want to grab from the cache
 * @param date: the date for which we want the people
 * @returns All the collaborators for the given date range in the form of {email, location, date}
 */
export function usePeopleFromCacheForDateRange(
    date: OwaDate,
    filterPeopleDataFunc?: (peopleInfo: PeopleInfo) => boolean
):
    | {
          email: UserEmailAddress;
          location: any;
          date: OwaDate;
      }[]
    | undefined {
    const currentOwner = getUserExternalDirectoryId();
    const currentTimeZone = getUserTimeZone();

    // Clear + re-sync the cache outside of the read-only liveQuery context when the
    // stored timezone no longer matches the current session timezone.
    usePlacesPeopleCacheMaintenance();

    return useLiveQuery(
        async () => {
            try {
                const timezoneSettings = await fetchTimezoneSettings(currentOwner);

                if (timezoneSettings?.timezone !== getUserTimeZone()) {
                    // Stale cache: return empty and let usePlacesPeopleCacheMaintenance
                    // clear it. Never write to Dexie here -- this runs in a read-only
                    // liveQuery transaction and any write throws ReadOnlyError.
                    return undefined;
                }

                const collaboratorEmails = await fetchCollaborators();

                const peopleData = await fetchPeopleDataForDateRange(
                    currentOwner,
                    date,
                    collaboratorEmails,
                    filterPeopleDataFunc
                );

                return peopleData.map(row => ({
                    email: row.email as UserEmailAddress,
                    location: row.location,
                    date: owaDate(getUserTimeZone(), row.startOfDay),
                }));
            } catch (error) {
                logGreyError(
                    'PlacesPeopleCache_usePeopleFromCacheForDateRange',
                    getModuleContextMailboxInfo(),
                    error instanceof Error ? error : undefined
                );
                return [];
            }
        },
        [
            currentOwner,
            getTimestamp(startOfDay(date)),
            filterPeopleDataFunc,
            currentTimeZone,
        ] /** deps: re-subscribe the querier when any non-Dexie input changes */,
        []
    );
}

/**
 * Fetch people data for a specific date range and filter by the given criteria.
 */
async function fetchPeopleDataForDateRange(
    currentOwner: string,
    date: OwaDate,
    collaboratorEmails: string[],
    filterPeopleDataFunc?: (peopleInfo: PeopleInfo) => boolean
) {
    const peopleData = await placesPeopleTable
        .where({
            owner: currentOwner,
            startOfDay: getTimestamp(startOfDay(date)),
        })
        .toArray();

    const seen = new Set();
    return peopleData
        .filter(item => {
            const key = `${item.email}-${item.date}`;
            if (seen.has(key)) {
                return false;
            } else {
                seen.add(key);
                return true;
            }
        })
        .filter(row => collaboratorEmails.includes(row.email))
        .filter(filterPeopleDataFunc ?? (() => true));
}
